// ============================================================================
// INDEX.JS - PUNTO DE ENTRADA DE LA APLICACIÓN TASK MANAGEMENT
// ============================================================================

// PRIMERO de todo: fija el control de consola (silencia logs en prod y pinta el
// banner de versión) antes de que cualquier otro módulo ejecute sus console.log.
import './utils/consoleControl';
import React from 'react';
import { createRoot } from 'react-dom/client';
import './styles/index.css';
import './styles/layout.css';
import './styles/sprint.css';
import './styles/tasks.css';
import App from './App';
import appConfig from './config/appConfig';

// Error boundary para capturar errores globales
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Actualiza el state para mostrar la UI de error
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('🚨 Error capturado por ErrorBoundary:', error, errorInfo);
    
    // Aquí puedes enviar el error a un servicio de monitoreo
    // como Sentry, LogRocket, etc.
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Ejemplo de envío a servicio de monitoreo
    if (process.env.NODE_ENV === 'production') {
      // Sentry.captureException(error, { extra: errorInfo });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h1 className="error-title">Oops! Algo salió mal</h1>
            <p className="error-message">
              Ha ocurrido un error inesperado en la aplicación.
            </p>
            
            {process.env.NODE_ENV === 'development' && (
              <details className="error-details">
                <summary>Detalles del error (desarrollo)</summary>
                <pre className="error-stack">
                  {this.state.error && this.state.error.toString()}
                  <br />
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
            
            <div className="error-actions">
              <button 
                onClick={() => window.location.reload()}
                className="error-reload-btn"
              >
                🔄 Recargar página
              </button>
              <a 
                href="mailto:jvseoweb@gmail.com?subject=Error en Task Management"
                className="error-support-link"
              >
                📧 Contactar soporte
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Función para reportar web vitals (opcional)
const reportWebVitals = (onPerfEntry) => {
  if (onPerfEntry && onPerfEntry instanceof Function) {
    import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
      getCLS(onPerfEntry);
      getFID(onPerfEntry);
      getFCP(onPerfEntry);
      getLCP(onPerfEntry);
      getTTFB(onPerfEntry);
    });
  }
};

// Función principal de inicialización
const initializeApp = () => {
  try {
    // NOTA: La configuración ya se cargó síncronamente en index.html
    // usando window.__APP_CONFIG__ antes de que este script se ejecute

    console.log('🚀 Inicializando Task Management App...');
    console.log('📋 Configuración:', {
      CLIENT_NAME: appConfig.getClientName(),
      ENVIRONMENT: appConfig.getEnvironment(),
      API_URL: appConfig.getApiUrl()
    });

    // 1. Verificar que el container existe
    const container = document.getElementById('root');
    if (!container) {
      throw new Error('No se encontró el elemento root en el DOM');
    }

    // 2. Crear la raíz de React 18
    const root = createRoot(container);

    // 3. Renderizar la aplicación con error boundary
    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </React.StrictMode>
    );

    console.log('✅ Task Management App initialized successfully');

    // Reportar web vitals en desarrollo
    if (process.env.NODE_ENV === 'development') {
      reportWebVitals(console.log);
    }

  } catch (error) {
    console.error('💥 Error fatal inicializando la aplicación:', error);
    
    // Fallback si no se puede inicializar React
    const container = document.getElementById('root');
    if (container) {
      container.innerHTML = `
        <div style="
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          font-family: system-ui, -apple-system, sans-serif;
          background: #f8fafc;
          color: #1e293b;
          text-align: center;
          padding: 2rem;
        ">
          <div style="
            background: white;
            padding: 3rem;
            border-radius: 12px;
            box-shadow: 0 10px 25px rgba(0,0,0,0.1);
            max-width: 500px;
          ">
            <div style="font-size: 4rem; margin-bottom: 1rem;">💥</div>
            <h1 style="margin-bottom: 1rem; color: #ef4444;">Error Fatal</h1>
            <p style="margin-bottom: 2rem; color: #64748b; line-height: 1.6;">
              No se pudo inicializar la aplicación Task Management. 
              Por favor, recarga la página o contacta con soporte.
            </p>
            <button 
              onclick="window.location.reload()" 
              style="
                background: #3b82f6;
                color: white;
                border: none;
                padding: 12px 24px;
                border-radius: 8px;
                font-weight: 600;
                cursor: pointer;
                margin-right: 1rem;
              "
            >
              🔄 Recargar página
            </button>
            <a 
              href="mailto:jvseoweb@gmail.com?subject=Error Fatal en Task Management"
              style="
                background: transparent;
                color: #64748b;
                border: 1px solid #e2e8f0;
                padding: 12px 24px;
                border-radius: 8px;
                font-weight: 600;
                text-decoration: none;
                display: inline-block;
              "
            >
              📧 Contactar soporte
            </a>
          </div>
        </div>
      `;
    }
  }
};

// Inicializar cuando el DOM esté listo
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeApp);
} else {
  initializeApp();
}

// Exportar para testing si es necesario
export { ErrorBoundary, reportWebVitals };